import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import MobileScreenshotsGallery from '../components/MobileScreenshotsGallery';
import ControllerScreenshotsGallery from '../components/ControllerScreenshotsGallery';
import WebAdminScreenshotsGallery from '../components/WebAdminScreenshotsGallery';
import SEO from '../components/SEO';

const DocumentationPage = () => {
  return (
    <>
      <SEO
        title="Documentation utilisateur - Tunisie Parking | Guide complet"
        description="Guide complet pour utiliser le système Tunisie Parking. Documentation détaillée pour l'application mobile, l'application contrôleur et le Web Admin. FAQ et tutoriels inclus."
        keywords="documentation tunisie parking, guide parking tunisie, tutoriel parking, FAQ parking, aide parking tunisie, manuel utilisateur parking"
        url="https://parking.tn/documentation"
      />
      <div className="min-h-screen bg-gray-50">
      {/* Header avec navigation */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl sm:text-6xl font-bold mb-4">Documentation utilisateur</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Guide complet pour utiliser le système Tunisie Parking
            </p>
          </div>
        </div>
      </section>

      {/* Table des matières */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex flex-wrap gap-4 text-sm">
            <a href="#introduction" className="text-gray-600 hover:text-primary-600 transition-colors">Introduction</a>
            <a href="#app-mobile" className="text-gray-600 hover:text-primary-600 transition-colors">App mobile</a>
            <a href="#app-controleur" className="text-gray-600 hover:text-primary-600 transition-colors">App contrôleur</a>
            <a href="#web-admin" className="text-gray-600 hover:text-primary-600 transition-colors">Web admin</a>
            <a href="#faq" className="text-gray-600 hover:text-primary-600 transition-colors">FAQ</a>
          </nav>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Introduction */}
        <section id="introduction" className="mb-16 scroll-mt-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Introduction</h2>
          
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Qu'est-ce que Tunisie Parking ?</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Tunisie Parking</strong> est un système de parking intelligent conçu spécifiquement pour la Tunisie. 
              Il révolutionne la gestion du stationnement en offrant une solution complète et moderne qui simplifie 
              la recherche, le paiement et le contrôle des places de parking.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Le système est composé de <strong>trois applications principales</strong> :
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
              <li><strong>Application mobile</strong> : Pour les utilisateurs qui cherchent et paient des places de parking</li>
              <li><strong>Application contrôleur</strong> : Pour les agents municipaux qui vérifient les tickets de stationnement</li>
              <li><strong>Web admin</strong> : Pour les administrateurs qui gèrent l'ensemble du système</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
            <h4 className="text-lg font-semibold text-blue-900 mb-2">🎯 Avantages</h4>
            <ul className="list-disc list-inside text-blue-800 space-y-1">
              <li>Recherche rapide de parkings disponibles près de vous</li>
              <li>Paiement sécurisé et instantané depuis votre smartphone</li>
              <li>Gestion simplifiée de vos tickets de stationnement</li>
              <li>Contrôle automatisé et efficace pour les agents</li>
              <li>Transparence totale sur vos transactions</li>
            </ul>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
            <h4 className="text-lg font-semibold text-green-900 mb-2">✨ Fonctionnalités principales</h4>
            <div className="grid md:grid-cols-2 gap-4 text-green-800">
              <div>
                <p className="font-semibold mb-2">Pour les utilisateurs :</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Géolocalisation en temps réel des parkings</li>
                  <li>Création et gestion de tickets de stationnement</li>
                  <li>Portefeuille électronique intégré</li>
                  <li>Paiements sécurisés</li>
                  <li>Notifications en temps réel</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Pour les contrôleurs :</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Scan automatique de plaques d'immatriculation</li>
                  <li>Vérification instantanée des tickets</li>
                  <li>Historique complet des contrôles</li>
                  <li>Interface optimisée pour le terrain</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-2">Pour les administrateurs :</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Dashboard analytics en temps réel</li>
                  <li>Gestion complète des parkings et zones</li>
                  <li>Administration des utilisateurs</li>
                  <li>Rapports détaillés et exports</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Application Mobile */}
        <section id="app-mobile" className="mb-16 scroll-mt-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Application mobile utilisateur</h2>
          
          {/* Carousel des captures d'écran */}
          <div className="mb-8">
            <MobileScreenshotsGallery />
          </div>

          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <span className="text-4xl mr-4">📱</span>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">Guide d'utilisation complet</h3>
                <p className="text-gray-600">Tout ce que vous devez savoir pour utiliser l'application mobile</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">1. Créer un compte</h4>
                <p className="text-gray-700 text-sm mb-2">Téléchargez l'application, entrez votre numéro de téléphone tunisien, et validez avec le code OTP reçu par SMS. Votre compte est créé automatiquement.</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">2. Ajouter un véhicule</h4>
                <p className="text-gray-700 text-sm mb-2">Dans l'onglet "Profil", allez dans "Mes Véhicules" et ajoutez votre plaque d'immatriculation tunisienne. Vous pouvez ajouter plusieurs véhicules.</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">3. Recharger votre portefeuille</h4>
                <p className="text-gray-700 text-sm mb-2">Dans l'onglet "Wallet", cliquez sur "Recharger", choisissez le montant et payez via Flouci/D17. Le montant est crédité instantanément.</p>
              </div>
              <div className="border-l-4 border-orange-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">4. Créer un ticket</h4>
                <p className="text-gray-700 text-sm mb-2">Sur la carte, trouvez un parking, cliquez dessus, sélectionnez votre véhicule et la durée. Le paiement se fait automatiquement depuis votre wallet.</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">5. Prolonger un ticket</h4>
                <p className="text-gray-700 text-sm mb-2">Dans "Tickets", sélectionnez votre ticket actif et cliquez sur "Prolonger". Choisissez la durée supplémentaire et confirmez.</p>
              </div>
              <div className="border-l-4 border-yellow-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">6. Consulter l'historique</h4>
                <p className="text-gray-700 text-sm mb-2">Consultez tous vos tickets et transactions dans les onglets respectifs. Accédez à vos statistiques dans votre profil.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Application Contrôleur */}
        <section id="app-controleur" className="mb-16 scroll-mt-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Application contrôleur</h2>
          
          {/* Carousel des captures d'écran */}
          <div className="mb-8">
            <ControllerScreenshotsGallery />
          </div>

          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <span className="text-4xl mr-4">🚗</span>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">Guide d'utilisation pour les contrôleurs</h3>
                <p className="text-gray-600">Tout ce que vous devez savoir pour effectuer des contrôles efficacement</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">1. Se connecter</h4>
                <p className="text-gray-700 text-sm mb-2">Ouvrez l'application, entrez vos identifiants de contrôleur, et validez avec le code OTP reçu par SMS.</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">2. Scanner une plaque</h4>
                <p className="text-gray-700 text-sm mb-2">Activez le mode scan automatique et pointez la caméra vers la plaque. L'application détecte automatiquement la plaque et affiche les informations du ticket.</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">3. Saisie manuelle</h4>
                <p className="text-gray-700 text-sm mb-2">Si le scan ne fonctionne pas, utilisez la saisie manuelle. Sélectionnez le type de plaque et entrez le numéro.</p>
              </div>
              <div className="border-l-4 border-orange-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">4. Vérifier un ticket</h4>
                <p className="text-gray-700 text-sm mb-2">Après le scan, vous verrez le statut du ticket, la durée restante, le parking utilisé, et toutes les informations nécessaires.</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">5. Consulter l'historique</h4>
                <p className="text-gray-700 text-sm mb-2">Dans l'onglet "Historique", consultez tous vos contrôles de la journée avec filtres et statistiques.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Web Admin */}
        <section id="web-admin" className="mb-16 scroll-mt-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Web admin</h2>
          
          {/* Carousel des captures d'écran */}
          <div className="mb-8">
            <WebAdminScreenshotsGallery />
          </div>

          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <span className="text-4xl mr-4">💼</span>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">Guide d'utilisation pour les administrateurs</h3>
                <p className="text-gray-600">Tout ce que vous devez savoir pour gérer le système</p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">1. Se connecter</h4>
                <p className="text-gray-700 text-sm mb-2">Accédez à l'interface Web Admin, entrez vos identifiants administrateur, et validez avec le code OTP.</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">2. Consulter le dashboard</h4>
                <p className="text-gray-700 text-sm mb-2">Le dashboard affiche en temps réel les revenus, tickets actifs, occupation des parkings, et graphiques d'évolution.</p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">3. Gérer les parkings</h4>
                <p className="text-gray-700 text-sm mb-2">Créez, modifiez ou désactivez des parkings. Configurez les tarifs, zones, et géolocalisation.</p>
              </div>
              <div className="border-l-4 border-orange-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">4. Gérer les utilisateurs</h4>
                <p className="text-gray-700 text-sm mb-2">Consultez tous les utilisateurs, modifiez leurs rôles (USER, CONTROLLER, ADMIN), et consultez leurs profils.</p>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">5. Suivre les tickets</h4>
                <p className="text-gray-700 text-sm mb-2">Consultez tous les tickets actifs et historiques avec filtres avancés par parking, utilisateur, véhicule, ou date.</p>
              </div>
              <div className="border-l-4 border-yellow-500 pl-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">6. Générer des rapports</h4>
                <p className="text-gray-700 text-sm mb-2">Générez des rapports de revenus, occupation, utilisation, et exportez-les en CSV ou PDF.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="mb-16 scroll-mt-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">FAQ - Questions fréquentes</h2>
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Comment créer un compte utilisateur ?</h3>
              <p className="text-gray-700">
                Ouvrez l'application mobile, entrez votre numéro de téléphone tunisien, et entrez le code OTP reçu par SMS. 
                Votre compte sera créé automatiquement lors de la première connexion.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Comment recharger mon wallet ?</h3>
              <p className="text-gray-700">
                Dans l'application mobile, allez dans l'onglet "Wallet", cliquez sur "Recharger", choisissez le montant 
                et payez via Flouci/D17. Le montant est crédité instantanément.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Puis-je prolonger un ticket en cours ?</h3>
              <p className="text-gray-700">
                Oui, dans l'application mobile, allez dans "Tickets", sélectionnez votre ticket actif et cliquez sur "Prolonger". 
                Le paiement se fait automatiquement depuis votre wallet.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Que se passe-t-il si mon ticket expire ?</h3>
              <p className="text-gray-700">
                Vous recevrez une notification avant l'expiration. Si le ticket expire, vous pouvez le prolonger immédiatement 
                ou créer un nouveau ticket. Notez que les contrôleurs peuvent vérifier les tickets expirés.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Comment les contrôleurs scannent-ils les plaques ?</h3>
              <p className="text-gray-700">
                Les contrôleurs utilisent l'application contrôleur. Ils pointent la caméra vers la plaque d'immatriculation, 
                et l'application scanne automatiquement avec reconnaissance OCR. Ils peuvent aussi saisir manuellement en cas de besoin.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Que faire si je perds mon téléphone ?</h3>
              <p className="text-gray-700">
                Contactez le support immédiatement. Votre compte peut être sécurisé et vos tickets actifs peuvent être transférés 
                vers un nouveau téléphone après vérification d'identité.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Puis-je utiliser plusieurs véhicules avec le même compte ?</h3>
              <p className="text-gray-700">
                Oui, vous pouvez ajouter plusieurs véhicules dans votre profil et les sélectionner lors de la création d'un ticket. 
                Chaque véhicule peut avoir ses propres tickets.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Comment devenir contrôleur ou administrateur ?</h3>
              <p className="text-gray-700">
                Les rôles CONTROLLER et ADMIN sont attribués uniquement par les administrateurs existants via le Web Admin. 
                Contactez votre administrateur système pour obtenir ces accès.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">L'application fonctionne-t-elle hors-ligne ?</h3>
              <p className="text-gray-700">
                L'application contrôleur supporte un mode hors-ligne limité pour les scans. Les données sont synchronisées 
                automatiquement dès que la connexion est rétablie. L'application mobile nécessite une connexion pour la plupart des fonctionnalités.
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Comment contacter le support ?</h3>
              <p className="text-gray-700">
                Vous pouvez nous contacter via la page Contact du site web, ou envoyer un email à 
                <strong> hello@buildmybrand.art</strong>. Nous répondons généralement sous 24h.
              </p>
            </div>
          </div>
        </section>

        {/* Support */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-primary-600 to-primary-700 text-white rounded-xl shadow-lg p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Besoin d'aide ?</h2>
            <p className="text-xl text-primary-100 mb-6">
              Notre équipe est là pour vous aider. Contactez-nous pour toute question ou problème.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-6 py-3 bg-white text-primary-700 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
              >
                Page de Contact
              </Link>
              <a
                href="mailto:hello@buildmybrand.art"
                className="px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-400 transition-colors"
              >
                Email support
              </a>
            </div>
          </div>
        </section>

      </div>

      <Footer />
    </div>
    </>
  );
};

export default DocumentationPage;
